import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Navigation } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { serviceRoleClient } from '../../services/serviceRoleClient';

const db = () => serviceRoleClient || supabase;

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const loadLeaflet = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.L) return Promise.resolve(window.L);
  if (window.__roadserveLeafletPromise) return window.__roadserveLeafletPromise;
  window.__roadserveLeafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = LEAFLET_CSS;
      document.head.appendChild(css);
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => (window.L ? resolve(window.L) : reject(new Error('Leaflet failed')));
    script.onerror = () => reject(new Error('Leaflet blocked'));
    document.body.appendChild(script);
  });
  return window.__roadserveLeafletPromise;
};

const parseCoord = (lat, lng) => {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a === 0 && b === 0) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return { lat: a, lng: b };
};

const haversineKm = (a, b) => {
  if (!a || !b) return null;
  const toRad = (n) => (n * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const formatKm = (km) => {
  if (km == null || !Number.isFinite(km)) return '—';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(km < 10 ? 1 : 0)} km`;
};

const extractLatLngFromText = (text) => {
  const s = String(text || '');
  const at = s.match(/@(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
  if (at) return parseCoord(at[1], at[2]);
  const q = s.match(/[?&]q=(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
  if (q) return parseCoord(q[1], q[2]);
  const ll = s.match(/(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/);
  if (ll) return parseCoord(ll[1], ll[2]);
  return null;
};

const fmtTime = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function ComplaintMechanicMap({ complaint, mechanicRow, embedded = false, compact = false }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const [mapError, setMapError] = useState('');

  const status = String(complaint?.request_status || complaint?.status || '').toLowerCase();
  const isCancelled = status.includes('cancel');
  const isClosed = isCancelled || status.includes('complete') || status.includes('closed');
  const isLive = !isClosed && !!complaint?.assigned_mechanic_id;

  const customerPin = useMemo(() => {
    return (
      parseCoord(complaint?.pickup_latitude, complaint?.pickup_longitude) ||
      parseCoord(complaint?.latitude, complaint?.longitude) ||
      extractLatLngFromText(complaint?.location_link) ||
      extractLatLngFromText(complaint?.pickup_address) ||
      extractLatLngFromText(complaint?.address)
    );
  }, [complaint]);

  const livePin = useMemo(() => {
    return (
      parseCoord(complaint?.partner_live_lat, complaint?.partner_live_lng) ||
      parseCoord(mechanicRow?.service_center_lat, mechanicRow?.service_center_lng) ||
      parseCoord(complaint?.mechanic_latitude, complaint?.mechanic_longitude)
    );
  }, [complaint, mechanicRow]);

  const { data: pings = [] } = useQuery({
    queryKey: ['complaintLocationTrail', complaint?.id],
    queryFn: async () => {
      const { data, error } = await db()
        .from('rsa_job_location_pings')
        .select('id, latitude, longitude, created_at')
        .eq('service_request_id', complaint.id)
        .order('created_at', { ascending: true })
        .limit(500);
      if (error) return [];
      return (data || [])
        .map((p) => ({ ...p, ...parseCoord(p.latitude, p.longitude) }))
        .filter((p) => p.lat != null);
    },
    enabled: !!complaint?.id,
    refetchInterval: isLive ? 12000 : false,
  });

  const lastPing = pings.length ? pings[pings.length - 1] : null;
  const mechanicPin = lastPing ? { lat: lastPing.lat, lng: lastPing.lng } : livePin;

  const traveledKm = useMemo(() => {
    if (pings.length < 2) return 0;
    let sum = 0;
    for (let i = 1; i < pings.length; i += 1) {
      sum += haversineKm(pings[i - 1], pings[i]) || 0;
    }
    return sum;
  }, [pings]);

  const remainingKm = haversineKm(mechanicPin, customerPin);
  const startPin = pings[0] || mechanicPin;
  const fromStartKm = haversineKm(startPin, customerPin);

  useEffect(() => {
    let cancelled = false;
    if (!mapEl.current) return undefined;
    if (!customerPin && !mechanicPin) return undefined;

    loadLeaflet()
      .then((L) => {
        if (cancelled || !mapEl.current) return;
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
        const map = L.map(mapEl.current, { scrollWheelZoom: true });
        mapRef.current = map;
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap',
        }).addTo(map);

        const bounds = [];
        if (customerPin) {
          L.marker([customerPin.lat, customerPin.lng], { title: 'Customer' })
            .addTo(map)
            .bindPopup('Customer / pickup');
          bounds.push([customerPin.lat, customerPin.lng]);
        }
        if (mechanicPin) {
          L.circleMarker([mechanicPin.lat, mechanicPin.lng], {
            radius: 9,
            color: isCancelled ? '#dc2626' : '#0d9488',
            fillColor: isCancelled ? '#ef4444' : '#14b8a6',
            fillOpacity: 0.95,
          })
            .addTo(map)
            .bindPopup(isCancelled ? 'Last mechanic location' : 'Mechanic live / last known');
          bounds.push([mechanicPin.lat, mechanicPin.lng]);
        }
        if (pings.length >= 2) {
          const line = pings.map((p) => [p.lat, p.lng]);
          L.polyline(line, { color: isCancelled ? '#f97316' : '#2563eb', weight: 4, opacity: 0.85 }).addTo(map);
          line.forEach((pt) => bounds.push(pt));
        } else if (customerPin && mechanicPin) {
          L.polyline(
            [
              [mechanicPin.lat, mechanicPin.lng],
              [customerPin.lat, customerPin.lng],
            ],
            { color: '#94a3b8', weight: 2, dashArray: '6 6' },
          ).addTo(map);
        }
        if (bounds.length === 1) {
          map.setView(bounds[0], 14);
        } else if (bounds.length > 1) {
          map.fitBounds(bounds, { padding: [28, 28], maxZoom: 15 });
        }
      })
      .catch((e) => {
        if (!cancelled) setMapError(e.message || 'Map failed to load');
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [customerPin?.lat, customerPin?.lng, mechanicPin?.lat, mechanicPin?.lng, pings, isCancelled]);

  if (!customerPin && !mechanicPin) {
    return (
      <div className={embedded ? 'py-4 text-center' : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6'}>
        {!embedded ? (
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={20} className="text-teal-600" />
            <h2 className="text-xl font-bold text-gray-900">Mechanic map</h2>
          </div>
        ) : null}
        <p className="text-sm text-gray-500">
          Is complaint par GPS pin nahi hai (customer location / mechanic live). Naye assigned jobs par partner
          app live location bhejti hai.
        </p>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6'}>
      {!embedded ? (
        <div className="flex items-center gap-2 mb-3">
          <Navigation size={20} className="text-teal-600" />
          <h2 className="text-xl font-bold text-gray-900">Mechanic map</h2>
          {isLive ? (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
              Live
            </span>
          ) : isCancelled ? (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
              Cancelled trail
            </span>
          ) : (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
              Route history
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-3">
          {isLive ? (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">Live</span>
          ) : isCancelled ? (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Cancelled trail</span>
          ) : (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">Route history</span>
          )}
        </div>
      )}

      <div className={`grid grid-cols-3 gap-2 mb-2 text-sm ${compact ? '' : 'sm:grid-cols-3 gap-3 mb-4'}`}>
        <div className="rounded-md bg-slate-50 p-2">
          <p className="text-[10px] text-gray-500">Travelled</p>
          <p className="font-semibold text-gray-900">{formatKm(traveledKm)}</p>
          <p className="text-[10px] text-gray-400">{pings.length} pings</p>
        </div>
        <div className="rounded-md bg-slate-50 p-2">
          <p className="text-[10px] text-gray-500">{isCancelled ? 'Gap left' : 'Distance left'}</p>
          <p className="font-semibold text-gray-900">{formatKm(remainingKm)}</p>
          {complaint?.partner_live_updated_at ? (
            <p className="text-[10px] text-gray-400">{fmtTime(complaint.partner_live_updated_at)}</p>
          ) : lastPing ? (
            <p className="text-[10px] text-gray-400">{fmtTime(lastPing.created_at)}</p>
          ) : null}
        </div>
        <div className="rounded-md bg-slate-50 p-2">
          <p className="text-[10px] text-gray-500">Start distance</p>
          <p className="font-semibold text-gray-900">{formatKm(fromStartKm)}</p>
        </div>
      </div>

      {mapError ? (
        <p className="text-sm text-red-600">{mapError}</p>
      ) : (
        <div ref={mapEl} className={`${compact ? 'h-64' : embedded ? 'h-[420px]' : 'h-80'} w-full rounded-lg overflow-hidden border border-gray-200 z-0`} />
      )}
      {!pings.length && mechanicPin ? (
        <p className="mt-2 text-xs text-gray-500">
          Full route trail naye jobs se save hoga. Abhi last known / live pin dikh raha hai.
        </p>
      ) : null}
    </div>
  );
}
